import { EditorState, Modifier, SelectionState, convertToRaw } from 'draft-js';
import { getEntities, translateVariable } from '../components/RichEditor/utils';
import {
  isATableBox,
  isATextBox,
  isATitleBox,
  isAVariableBox,
  simulateVariableGroupBox,
  simulateVariableTextBox,
} from './boxes';
import { ItemTypes, variableHandlingType } from '../constants/constants';
import {
  replaceVariablesInPlainText,
  transformTextToEditorState,
} from './dataSync';
import { shouldHideSectionVariable } from './sections';
import { addressAggregateKey, addressPartKeys, composeAddress } from './address';

const replaceVariableText = ({
  entity,
  variables,
  blockKey,
  start,
  end,
  contentState,
  entityKey,
  editorState,
  isTemplate = false,
}) => {
  let newContentState = contentState;
  let modifiedEditorState = editorState;
  // `lineStyle` et `separator` sont portés par l'occurrence : deux insertions
  // de la même variable peuvent se rendre différemment.
  const { key, lineStyle, separator } = entity.getData();
  // Clé absente du document : rien à écrire, on laisse le libellé en place.
  // translateVariable rend déjà null dans ce cas ; sans cette sortie, la ligne
  // `variables[key].notUsed` plus bas lève sur un document qui ne connaît pas
  // la variable (contenu importé d'un autre périmètre, p. ex. un modèle
  // d'opportunité injecté dans une offre).
  if (!variables[key]) return { modifiedEditorState, newContentState };
  const variableTranslated = translateVariable({
    variableKey: key,
    variables,
    isTemplate,
    lineStyle,
    separator,
  });
  if (variables[key].notUsed) {
    delete variables[key].notUsed;
  }
  const selectionState = SelectionState.createEmpty(blockKey).merge({
    anchorOffset: start,
    focusOffset: end,
  });
  const contentBlock = newContentState.getBlockForKey(blockKey);
  newContentState = Modifier.replaceText(
    newContentState,
    selectionState,
    variableTranslated,
    contentBlock.getInlineStyleAt(start),
    entityKey
  );
  modifiedEditorState = EditorState.push(
    modifiedEditorState,
    newContentState,
    'insert-characters'
  );
  return { newContentState, modifiedEditorState };
};

export const replaceVariables = (editorState, variables, isTemplate = false) => {
  if (!editorState) return editorState;
  // Traiter les entités en ordre décroissant par position dans le bloc pour éviter
  // les décalages d'index lors de remplacements successifs dans un même bloc
  const entities = getEntities(editorState).sort((a, b) => {
    if (a.blockKey === b.blockKey) return b.start - a.start;
    // Conserver l'ordre inter-blocs (peu importe) car les offsets sont indépendants
    return 0;
  });
  const entitiesLength = entities.length;
  let newContentState = editorState.getCurrentContent();
  let modifiedEditorState = editorState;

  for (let i = 0; i < entitiesLength; i += 1) {
    const { entityKey, start, end, blockKey } = entities[i];
    if (!entityKey) continue;
    try {
      const entity = newContentState.getEntity(entityKey);
      if (!entity) continue;
      if (entity.getType() === 'VARIABLE') {
        const {
          modifiedEditorState: newestEditorState,
          newContentState: newestContentState,
        } = replaceVariableText({
          entity,
          variables,
          blockKey,
          start,
          end,
          contentState: newContentState,
          entityKey,
          editorState: modifiedEditorState,
          isTemplate,
        });
        newContentState = newestContentState;
        modifiedEditorState = newestEditorState;
      }
      if (entity.getType() === 'IMAGE') {
        const { box, menuAncestorType, menuAncestorIds } = entity.getData();
        const newImageBox = {
          ...box,
          content: {
            ...box.content,
            src: replaceVariablesInPlainText(box.content.src, variables),
          },
        };
        newContentState = newContentState.replaceEntityData(entityKey, {
          box: newImageBox,
          menuAncestorType,
          menuAncestorIds,
        });
      }
    } catch (error) {
      console.warn('Error processing entity:', error);
      continue;
    }
  }
  return modifiedEditorState;
};

export const removeVariables = (editorState, variableKeys) => {
  const entities = getEntities(editorState);
  let newContentState = editorState.getCurrentContent();
  let modifiedEditorState = editorState;

  entities.reverse().forEach(({ entityKey, start, end, blockKey }) => {
    const entity = newContentState.getEntity(entityKey);
    if (entity.getType() !== 'VARIABLE') return;
    const { key } = entity.getData();
    if (!variableKeys.includes(key)) return;

    const selectionState = SelectionState.createEmpty(blockKey).merge({
      anchorOffset: start,
      focusOffset: end,
    });
    newContentState = Modifier.applyEntity(
      newContentState,
      selectionState,
      null
    );

    modifiedEditorState = EditorState.push(
      modifiedEditorState,
      newContentState,
      'apply-entity'
    );
  });
  return modifiedEditorState;
};

export const getVariablesGroupedByContainer = ({
  variableGroups,
  variables,
  boxes,
  containers,
  columns,
  rowContainers = [],
}) => {
  const formBoxes = boxes.filter((b) => isAVariableBox(b.type));
  const variablesBoxes = formBoxes.filter((b) => !b.onlyForm);
  const onlyFormBoxes = formBoxes.filter((b) => b.onlyForm);
  const variablesGroupedByContainer = [];
  const orphanCustomTextVariables = {
    ...variables,
  };

  // Adresse découpée listée dans un groupement (« Établissement »…) : elle y
  // reste, ses quatre parties rattachées en sous-groupe (`addressParts`), et son
  // groupement dédié s'efface. Les parties ne deviennent pas membres du
  // groupement : en modèle, FormGroupArray renvoie ses membres au back.
  const nestedAddressKeys = new Set(
    variableGroups.flatMap((vb) => vb.variableIds.filter((vId) => variables[vId]?.derived))
  );

  variableGroups.forEach((vb) => {
    if (nestedAddressKeys.has(vb.id)) return;
    const variableIds = [];
    vb.variableIds.forEach((vId) => {
      if (orphanCustomTextVariables[vId]) {
        variableIds.push(vId);
        delete orphanCustomTextVariables[vId];
        if (variables[vId].derived) {
          addressPartKeys(vId).forEach((partKey) => delete orphanCustomTextVariables[partKey]);
        }
      }
    });

    // Groupement d'adresse (son id est la clé agrégée) : ses champs ne sont
    // jamais insérés tels quels dans le document, leur usage suit donc celui de
    // l'agrégat — sinon le bloc resterait masqué après insertion de l'adresse.
    const addressAggregate = variables[vb.id]?.derived ? variables[vb.id] : null;

    // Créer une seule entrée avec toutes les variables
    // Note: fromSectionIds sera calculé de manière lazy dans les composants qui en ont besoin
    // pour éviter de dégrader les performances de getVariablesGroupedByContainer
    const entry = {
      containerId: vb.id,
      variableGroupBox: {
        ...simulateVariableGroupBox(vb),
        notUsed: addressAggregate
          ? !!addressAggregate.notUsed
          : !vb.variableIds.some(
              (id) => variables[id] && !variables[id]?.notUsed
            ),
      },
      type: variableHandlingType.VARIABLE,
      variablesBoxes: variableIds
        .filter((vId) => variables[vId])
        .map((vId) => {
          const variable = addressAggregate
            ? { ...variables[vId], notUsed: addressAggregate.notUsed }
            : variables[vId];
          const box = simulateVariableTextBox([vId, variable]);
          if (!variable.derived) return box;
          return {
            ...box,
            addressParts: addressPartKeys(vId).map((partKey) =>
              simulateVariableTextBox([partKey, { ...variables[partKey], notUsed: variable.notUsed }])
            ),
          };
        }),
    };

    variablesGroupedByContainer.push(entry);
  });

  Object.entries(orphanCustomTextVariables).forEach(([key, variable]) => {
    // Clé agrégée d'une adresse découpée : recomposée depuis ses quatre parties,
    // elle n'a pas de champ propre — le groupement homonyme la remplace.
    if (variable.derived) return;
    variablesGroupedByContainer.push({
      containerId: key,
      type: variableHandlingType.VARIABLE,
      variablesBoxes: [simulateVariableTextBox([key, variable])],
    });
  });

  // Créer des Maps pour les lookups O(1) au lieu de find() O(n)
  // Map: columnId -> container
  const containersByColumnId = new Map();
  containers.forEach((container) => {
    container.columnsIds.forEach((columnId) => {
      if (!containersByColumnId.has(columnId)) {
        containersByColumnId.set(columnId, container);
      }
    });
  });

  // Sous-colonnes des lignes imbriquées : rattachées au container top-level qui
  // les héberge, pour que leurs variables restent groupées et ordonnées avec
  // lui (variableOrderArray n'indexe que le top-level). Boucle à point fixe :
  // une ligne ne peut être résolue qu'une fois sa colonne parente connue.
  let unresolvedRows = rowContainers;
  let resolvedSome = true;
  while (unresolvedRows.length && resolvedSome) {
    resolvedSome = false;
    const stillUnresolved = [];
    for (const row of unresolvedRows) {
      const parentContainer = containersByColumnId.get(row.parentColumnId);
      if (!parentContainer) {
        stillUnresolved.push(row);
        continue;
      }
      for (const columnId of row.columnsIds) {
        if (!containersByColumnId.has(columnId)) {
          containersByColumnId.set(columnId, parentContainer);
        }
      }
      resolvedSome = true;
    }
    unresolvedRows = stillUnresolved;
  }

  // Map: columnId -> column
  const columnsById = new Map(columns.map((c) => [c.id, c]));

  // Map: containerId -> index dans variablesGroupedByContainer (pour les lookups rapides)
  const containerIndexMap = new Map();
  variablesGroupedByContainer.forEach((vInfos, index) => {
    if (!containerIndexMap.has(vInfos.containerId)) {
      containerIndexMap.set(vInfos.containerId, []);
    }
    containerIndexMap.get(vInfos.containerId).push({ index, vInfos });
  });

  variablesBoxes.forEach((box) => {
    const container = containersByColumnId.get(box.columnId);
    if (!container) return; // Skip si aucun container trouvé
    
    const boxContainerId = container.id;
    const column = columnsById.get(box.columnId);
    if (!column) return; // Skip si aucune colonne trouvée
    
    const drawMode = column.drawMode;
    
    // Utiliser la Map pour trouver rapidement l'index
    const containerEntries = containerIndexMap.get(boxContainerId) || [];
    const sameContainerIndex = containerEntries.findIndex(
      ({ vInfos }) => !drawMode && vInfos.containerId === boxContainerId
    );
    const actualIndex = sameContainerIndex >= 0 
      ? containerEntries[sameContainerIndex].index 
      : -1;

    if (actualIndex !== -1) {
      variablesGroupedByContainer[actualIndex].variablesBoxes.push(box);
    } else {
      const newEntry = {
        containerId: boxContainerId,
        type: variableHandlingType.FORM,
        variablesBoxes: [box],
      };
      variablesGroupedByContainer.push(newEntry);
      // Mettre à jour la Map pour les prochaines itérations
      if (!containerIndexMap.has(boxContainerId)) {
        containerIndexMap.set(boxContainerId, []);
      }
      containerIndexMap.get(boxContainerId).push({ 
        index: variablesGroupedByContainer.length - 1, 
        vInfos: newEntry 
      });
    }
  });
  variablesGroupedByContainer.sort((vA, vB) => {
    if (
      vA.type === variableHandlingType.FORM &&
      vB.type !== variableHandlingType.FORM
    )
      return 1;
    if (
      vA.type === variableHandlingType.FORM &&
      vB.type === variableHandlingType.FORM
    )
      return 0;
    if (
      vA.type !== variableHandlingType.FORM &&
      vB.type === variableHandlingType.FORM
    )
      return -1;
    if (vA.variableGroupBox && !vB.variableGroupBox) return -1;
    if (vA.variableGroupBox && vB.variableGroupBox) return 0;
    if (!vA.variableGroupBox && vB.variableGroupBox) return 1;
    return 0;
  });
  onlyFormBoxes.forEach((b) => {
    variablesGroupedByContainer.push({
      containerId: b.id,
      type: variableHandlingType.FORM,
      variablesBoxes: [b],
    });
  });
  return variablesGroupedByContainer;
};

export const isVariableCustom = (key) => {
  if (key === 'create-variable') return true;
  const firstKey = key.substring(0, key.lastIndexOf('.'));
  return firstKey === 'custom';
};

const editorStateHasEntity = (editorState, variableKey) => {
  if (!editorState) return false;
  const entities = getEntities(editorState);
  return entities.some(({ entityKey }) => {
    if (!entityKey) return false;
    try {
      const entity = editorState.getCurrentContent().getEntity(entityKey);
      if (!entity) return false;
      const { key } = entity.getData();
      return key === variableKey;
    } catch (error) {
      console.warn('Error accessing entity:', error);
      return false;
    }
  });
};

export const boxContainsVariable = (variableKey, box) => {
  if (
    (isATextBox(box.type) || isATitleBox(box.type)) &&
    box.content.editorState
  ) {
    if (editorStateHasEntity(box.content.editorState, variableKey)) return true;
  }
  if (isAVariableBox(box.type) && box.name) {
    if (editorStateHasEntity(box.name, variableKey)) return true;
    if (
      (box.type === ItemTypes.CHECKBOXS_VARIABLE ||
        box.type === ItemTypes.QUESTION) &&
      box.checkboxs.some((c) => editorStateHasEntity(c.label, variableKey))
    )
      return true;
    if (
      box.type === ItemTypes.SELECTOR_VARIABLE &&
      box.options.some((c) => editorStateHasEntity(c.label, variableKey))
    )
      return true;
  }
  if (isATableBox(box.type)) {
    if (
      (box.content.columnsDisposition || []).some((c) =>
        editorStateHasEntity(transformTextToEditorState(c.title), variableKey)
      )
    )
      return true;
    if (
      box.content.columns.some((c) =>
        editorStateHasEntity(transformTextToEditorState(c.title), variableKey)
      )
    )
      return true;
    if (
      box.content.data.some((d) => {
        const keys = Object.keys(d);
        return keys.some(
          (key) =>
            key !== 'key' &&
            editorStateHasEntity(
              transformTextToEditorState(d[key]),
              variableKey
            )
        );
      })
    )
      return true;
  }
  return false;
};

export const mutateInjectVariableTextContent = ({ textContent, variables }) => {
  textContent.content.editorState = replaceVariables(
    textContent.content.editorState,
    variables
  );
};

export const mutateInjectVariablesInBox = ({ variables, box }) => {
  if (isATextBox(box.type) || isATitleBox(box.type)) {
    mutateInjectVariableTextContent({ textContent: box, variables });
  }
  if (isAVariableBox(box.type)) {
    box.name = replaceVariables(box.name, variables);
    if (
      box.type === ItemTypes.CHECKBOXS_VARIABLE ||
      box.type === ItemTypes.QUESTION
    ) {
      box.checkboxs = box.checkboxs.map((c) => ({
        ...c,
        label: replaceVariables(c.label, variables),
      }));
    }

    if (box.type === ItemTypes.SELECTOR_VARIABLE) {
      box.options = box.options.map((c) => ({
        ...c,
        label: replaceVariables(c.label, variables),
      }));
    }
  }
  if (isATableBox(box.type)) {
    box.content.columns = box.content.columns.map((c) => ({
      ...c,
      title: JSON.stringify(
        convertToRaw(
          replaceVariables(
            transformTextToEditorState(c.title),
            variables
          ).getCurrentContent()
        )
      ),
    }));
    box.content.columnsDisposition = (box.content.columnsDisposition || []).map(
      (c) => ({
        ...c,
        title: JSON.stringify(
          convertToRaw(
            replaceVariables(
              transformTextToEditorState(c.title),
              variables
            ).getCurrentContent()
          )
        ),
      })
    );
    box.content.data = box.content.data.map((d) => {
      const keys = Object.keys(d);
      const newData = {};
      keys.forEach((key) => {
        if (key === 'key') {
          newData[key] = d[key];
        } else {
          newData[key] = JSON.stringify(
            convertToRaw(
              replaceVariables(
                transformTextToEditorState(d[key]),
                variables
              ).getCurrentContent()
            )
          );
        }
      });
      return newData;
    });
  }
  return box;
};

export const mutateInjectVariablesInDocument = ({
  variables,
  variable,
  variableKey,
  draft,
}) => {
  const boxes = [...draft.boxes, ...draft.masks.map((m) => m.boxes).flat()];
  boxes.forEach((b) => {
    if (!boxContainsVariable(variableKey, b)) return;
    mutateInjectVariablesInBox({
      variables: {
        ...variables,
        [variableKey]: variable,
      },
      box: b,
    });
  });
  Object.keys(draft.triggers).forEach((key) => {
    const type = draft.boxes.find((b) => b.id === key)?.type;
    if (type && isATextBox(type)) {
      draft.triggers[key].forEach((trigger) => {
        if (!trigger.result.hideField) {
          mutateInjectVariableTextContent({
            textContent: trigger.result,
            variables,
          });
        }
      });
    }
  });

  // Les parties d'une adresse ne sont pas posées dans le document, c'est la clé
  // agrégée qui l'est : saisir une ville doit donc recomposer l'agrégat et
  // injecter CELUI-LÀ. La récursion s'arrête là, un agrégat n'est pas une partie.
  const aggregateKey = addressAggregateKey(variableKey);
  const aggregate = aggregateKey ? draft.variables[aggregateKey] : null;
  if (aggregate) {
    aggregate.value = composeAddress(draft.variables, aggregateKey);
    // Une adresse éditée avant tout rechargement : sans ça filterUselessVariables
    // écarterait les parties encore marquées inutilisées et perdrait la saisie.
    delete aggregate.notUsed;
    addressPartKeys(aggregateKey).forEach((partKey) => {
      if (draft.variables[partKey]) delete draft.variables[partKey].notUsed;
    });
    mutateInjectVariablesInDocument({
      variables: draft.variables,
      variable: aggregate,
      variableKey: aggregateKey,
      draft,
    });
  }
};

// `notUsed` is overloaded — it can mean any of: (1) recursive field not referenced in the
// section content, (2) field absent from usedFieldKeys, (3) custom var the user hasn't inserted
// via @. All three collapse to "hide this row": a row is hidden when every box is notUsed/hidden.
export const shouldHideRowInForm = ({ vInfos, sectionList }) => {
  return (
    vInfos.variablesBoxes.some((b) =>
      shouldHideSectionVariable({
        type: b.type,
        sectionId: b.sectionId,
        sectionList,
        isActivatedByTrigger: b.isActivatedByTrigger,
      })
    ) ||
    vInfos.variablesBoxes.filter((b) => b.hideField || b.notUsed).length ===
      vInfos.variablesBoxes.length
  );
};
